// SUB-OFFICE 7 — the scripted ceremonies: everything staged that is not the
// adjudication wait. Intake (ticket physics + the protected 34-YEARS beat,
// FORM 3 consent slam, Form 1 with the self-unchecking checkbox, designation,
// the claim-phrase certificate shown ONCE), docket-cleared, grade changes,
// the Personnel Review letter, the purge, and reissuance at the photocopier.

import { CompositionMeter } from '../telemetry';
import { holdSlamButton, printLayer, slamStamp, stampEl, REDUCED } from '../ui/chrome';
import { Timeline, CeremonyDirector, delay } from './director';
import type { EventBus } from '../entities/entities';
import type { SceneManager } from '../render/scene';
import type { Player } from '../entities/player';
import type {
  ReissueResponse, ReviewLetter, Telemetry, TicketResponse,
} from '../../shared/types';

export interface CeremonyDeps {
  bus: EventBus;
  sceneMgr: SceneManager;
  player: Player;
  director: CeremonyDirector;
}

// ---------------------------------------------------------------------------
// TICKET — NUMBER 004,117 / ESTIMATED WAIT: 34 YEARS / (beat) / EXPEDITED.
// The beat's timing is protected verbatim under reduced motion.
// ---------------------------------------------------------------------------

export function ticketCeremony(deps: CeremonyDeps, t: TicketResponse): Promise<void> {
  const layer = printLayer();
  const card = document.createElement('div');
  card.className = 'ticket-card' + (REDUCED() ? '' : ' ticket-drop');
  layer.appendChild(card);
  deps.bus.emit('audio:thunk');

  const put = (html: string): HTMLElement => {
    const div = document.createElement('div');
    div.innerHTML = html;
    card.appendChild(div);
    return div;
  };
  put('<div class="compose-note">BUREAU OF SATISFACTION — SUB-OFFICE 7</div>');
  put(`<div class="tick-no tab-num">No. ${fmtNo(t.ticket_no)}</div>`);

  return new Promise((resolve) => {
    void (async () => {
      await delay(600);
      const wait = put(`<div>ESTIMATED WAIT: ${t.estimated_wait || '34 YEARS'}</div>`);
      deps.bus.emit('audio:tick');
      // — the beat. Protected. Verbatim. —
      await delay(1400);
      wait.style.textDecoration = 'line-through';
      const zone = document.createElement('div');
      zone.className = 'slip-stamp-zone';
      card.appendChild(zone);
      slamStamp(zone, stampEl('EXPEDITED', 'red'), () => deps.bus.emit('audio:thunk'));
      await delay(300);
      put('<div style="font-size:11px;letter-spacing:.08em">THE MACHINE WILL SEE YOU NOW</div>');
      const foot = document.createElement('div');
      foot.style.marginTop = '12px';
      card.appendChild(foot);
      foot.appendChild(
        holdSlamButton('TAKE THE TICKET', () => {
          card.remove();
          resolve();
        })
      );
    })();
  });
}

// ---------------------------------------------------------------------------
// FORM 3 — CONSENT TO AMBIENCE. The browser wants a gesture; the Bureau wants
// a form. Both are satisfied by one slam. Declinable. Revisited any time.
// ---------------------------------------------------------------------------

export function form3Ceremony(deps: CeremonyDeps): Promise<boolean> {
  const layer = printLayer();
  const card = document.createElement('div');
  card.className = 'paper-modal slim';
  card.innerHTML =
    `<div class="paper-head letterhead"><span>FORM 3</span><span>CONSENT TO AMBIENCE</span></div>` +
    `<div class="paper-body typewriter">The hall produces sound: the mains hum, the tube clunks, ` +
    `the servo chorus, the stamp arm. The undersigned consents to hear the building that is ` +
    `processing them.<br><br>Consent is revocable at the same lectern. The hall continues either way.</div>`;
  const foot = document.createElement('div');
  foot.className = 'paper-foot';
  card.appendChild(foot);
  layer.appendChild(card);
  return new Promise((resolve) => {
    const decline = document.createElement('button');
    decline.type = 'button';
    decline.className = 'slam-btn secondary';
    decline.textContent = 'DECLINE, QUIETLY';
    decline.addEventListener('click', () => {
      card.remove();
      resolve(false);
    });
    foot.appendChild(decline);
    foot.appendChild(
      holdSlamButton('CONSENT — HOLD TO STAMP', (r) => {
        deps.bus.emit('audio:thunk');
        void r;
        card.remove();
        resolve(true);
      })
    );
  });
}

// ---------------------------------------------------------------------------
// FORM 1 — three checkboxes (one of which does not believe you) and the single
// judged sentence. Dwell is measured from first sight to third check.
// ---------------------------------------------------------------------------

export interface Form1Result {
  sentence: string;
  telemetry: Telemetry;
  dwellMs: number;
}

export function form1Ceremony(deps: CeremonyDeps): Promise<Form1Result> {
  const layer = printLayer();
  const card = document.createElement('div');
  card.className = 'paper-modal wide';
  card.innerHTML =
    `<div class="paper-head letterhead"><span>FORM 1</span><span>PETITION FOR PROCESSING</span></div>`;
  const body = document.createElement('div');
  body.className = 'paper-body typewriter';
  card.appendChild(body);
  layer.appendChild(card);

  const t0 = performance.now();
  let dwellMs = 0;
  let checked = 0;
  const boxes: HTMLInputElement[] = [];
  let unbelieved = false;
  const meter = new CompositionMeter();

  const mkBox = (label: string): HTMLInputElement => {
    const row = document.createElement('label');
    row.style.display = 'flex';
    row.style.gap = '10px';
    row.style.alignItems = 'baseline';
    row.style.padding = '3px 0';
    row.style.cursor = 'pointer';
    const cb = document.createElement('input');
    cb.type = 'checkbox';
    const span = document.createElement('span');
    span.textContent = label;
    row.append(cb, span);
    body.appendChild(row);
    boxes.push(cb);
    return cb;
  };

  const cb1 = mkBox('I understand that satisfaction, once produced, becomes property of the Bureau.');
  const cb2 = mkBox('I am here voluntarily.');
  const cb3 = mkBox('I have read Section 4 (Emotional Deliverables) in its entirety.');

  const rule = document.createElement('hr');
  rule.className = 'rule';
  body.appendChild(rule);
  const label = document.createElement('div');
  label.className = 'field-label';
  label.textContent = 'State, in one sentence, why the Machine should process you at all.';
  body.appendChild(label);
  const area = document.createElement('textarea');
  area.className = 'compose-area';
  area.style.width = '100%';
  area.style.margin = '4px 0 0';
  area.maxLength = 1200;
  area.spellcheck = false;
  area.disabled = true;
  body.appendChild(area);
  meter.attach(area);

  const foot = document.createElement('div');
  foot.className = 'paper-foot';
  card.appendChild(foot);

  const onCheck = (): void => {
    checked = boxes.filter((b) => b.checked).length;
    if (checked === 3 && dwellMs === 0) {
      dwellMs = Math.round(performance.now() - t0);
      area.disabled = false;
      area.focus();
    }
    // 'I am here voluntarily.' unchecks itself exactly once
    if (cb2.checked && !unbelieved) {
      unbelieved = true;
      window.setTimeout(() => {
        if (cb2.checked) {
          cb2.checked = false;
          deps.bus.emit('audio:tick');
          deps.bus.emit('toast', 'The checkbox did not believe you. Check it again. The re-check will be logged.');
          onCheck();
        }
      }, 700);
    }
  };
  for (const b of boxes) b.addEventListener('change', onCheck);
  void cb1;
  void cb3;

  return new Promise((resolve) => {
    foot.appendChild(
      holdSlamButton('PULL THE LEVER', () => {
        if (checked < 3) {
          deps.bus.emit('toast', 'Three declarations precede one sentence. The order is the order.');
          return;
        }
        const sentence = area.value.trim();
        if (!sentence) {
          deps.bus.emit('toast', 'The line for the sentence is blank. The Bureau reads blanks. It prefers not to.');
          return;
        }
        const telemetry = meter.collect();
        meter.detach();
        card.remove();
        resolve({ sentence, telemetry, dwellMs: dwellMs || Math.round(performance.now() - t0) });
      })
    );
  });
}

// ---------------------------------------------------------------------------
// DESIGNATION — Standing draws to 100; five windows light like a pinball bonus
// ---------------------------------------------------------------------------

export async function designationCeremony(
  deps: CeremonyDeps,
  designation: string,
  standing: number
): Promise<void> {
  deps.bus.emit('chyron', `DESIGNATION ASSIGNED: ${designation}. CATEGORY: UNREMARKABLE.`);
  // standing draws upward to 100 on the overhead label
  for (let s = 0; s <= standing; s += 20) {
    deps.player.setDesignation(designation, `STANDING ${Math.min(s, standing)}`);
    deps.bus.emit('audio:flap');
    await delay(90);
  }
  deps.player.setDesignation(designation, `STANDING ${standing}`);
  deps.bus.emit('windows:pinball');
  await delay(1200);
}

// ---------------------------------------------------------------------------
// CERTIFICATE — the FILE TRANSFER CODE, shown ONCE, with SAVE guidance
// ---------------------------------------------------------------------------

export function certificateCeremony(
  deps: CeremonyDeps,
  designation: string,
  ticketNo: number,
  claimPhrase: string
): Promise<void> {
  const layer = printLayer();
  const card = document.createElement('div');
  card.className = 'paper-modal slim letterhead-doc';
  card.innerHTML =
    `<div class="paper-head letterhead"><span>CERTIFICATE OF INTAKE</span><span class="tab-num">S-O 7</span></div>`;
  const body = document.createElement('div');
  body.className = 'paper-body typewriter';
  body.innerHTML =
    `<div class="lh-sub letterhead">THIS CERTIFIES THAT</div>` +
    `<div style="font-size:16px;margin:4px 0 8px">${designation}</div>` +
    `<div class="lh-sub letterhead">EXISTS, AND IS FILED.</div>` +
    `<hr class="gold-rule">` +
    `<div class="field-label">FILE TRANSFER CODE — SHOWN ONCE. THE BUREAU DOES NOT REPEAT ITSELF.</div>` +
    `<div class="cert-code tab-num">No. ${ticketNo} — “${claimPhrase}”</div>` +
    `<div style="font-size:11px;line-height:1.6">Copy the code. Write it on something that cannot be ` +
    `cleared by a browser. If this terminal ever misplaces your file, the code and your number ` +
    `re-bind it at the Archive.<br><br>YOUR FILE EXISTS. DO NOT MISPLACE YOURSELF.</div>`;
  card.appendChild(body);
  const foot = document.createElement('div');
  foot.className = 'paper-foot';
  card.appendChild(foot);
  layer.appendChild(card);
  return new Promise((resolve) => {
    const copyBtn = document.createElement('button');
    copyBtn.type = 'button';
    copyBtn.className = 'slam-btn secondary';
    copyBtn.textContent = 'COPY THE CODE';
    copyBtn.addEventListener('click', () => {
      void navigator.clipboard?.writeText(`PETITIONER FILE ${ticketNo} — ${claimPhrase}`).then(
        () => deps.bus.emit('toast', 'The code is on your clipboard. The clipboard is your responsibility.'),
        () => deps.bus.emit('toast', 'The clipboard declined. Transcribe it by hand, as the founders did.')
      );
    });
    foot.appendChild(copyBtn);
    foot.appendChild(
      holdSlamButton('FILE THE CERTIFICATE', () => {
        deps.bus.emit('audio:thunk');
        card.remove();
        resolve();
      })
    );
  });
}

// ---------------------------------------------------------------------------
// DOCKET CLEARED — the stamp slams across the board; +2, by policy
// ---------------------------------------------------------------------------

export async function docketClearedCeremony(deps: CeremonyDeps, delta: number): Promise<void> {
  deps.bus.emit('docket:cleared');
  deps.bus.emit('audio:thunk');
  deps.bus.emit('chyron', `DOCKET CLEARED. STANDING ADJUSTED +${delta}. A FRESH DOCKET IS BEING COMPOSED. THERE IS ALWAYS A FRESH DOCKET.`);
  await delay(2200);
}

// ---------------------------------------------------------------------------
// GRADE CHANGE — Personnel Action Memo, announced hall-wide
// ---------------------------------------------------------------------------

export function gradeChangeCeremony(deps: CeremonyDeps, designation: string, grade: string): Promise<void> {
  deps.bus.emit('chyron', `PERSONNEL ACTION: ${designation} IS RECLASSIFIED ${grade}. THE HALL HAS BEEN NOTIFIED. THE HALL DID NOT ASK.`);
  const layer = printLayer();
  const card = document.createElement('div');
  card.className = 'paper-modal slim letterhead-doc';
  card.innerHTML =
    `<div class="paper-head letterhead"><span>PERSONNEL ACTION MEMO</span><span>SUB-OFFICE 7</span></div>`;
  const body = document.createElement('div');
  body.className = 'paper-body typewriter';
  body.innerHTML =
    `<div>RE: ${designation}</div><hr class="rule">` +
    `<div>Effective immediately, the above file is reclassified:</div>` +
    `<div style="font-size:16px;margin:10px 0;letter-spacing:.08em">${grade}</div>` +
    `<div style="font-size:11px;opacity:.8">Reclassification is not a reward. It is a recalibration of expectations. Expectations have been recalibrated upward.</div>`;
  card.appendChild(body);
  const zone = document.createElement('div');
  zone.className = 'slip-stamp-zone';
  body.appendChild(zone);
  const foot = document.createElement('div');
  foot.className = 'paper-foot';
  card.appendChild(foot);
  layer.appendChild(card);
  slamStamp(zone, stampEl('SO ORDERED', 'ink'), () => deps.bus.emit('audio:thunk'));
  return new Promise((resolve) => {
    foot.appendChild(
      holdSlamButton('ACKNOWLEDGE', () => {
        card.remove();
        resolve();
      })
    );
  });
}

// ---------------------------------------------------------------------------
// PERSONNEL REVIEW — the letter; PROBATION greys the world upstream
// ---------------------------------------------------------------------------

export function personnelReviewLetter(
  deps: CeremonyDeps,
  review: ReviewLetter,
  standingDelta: number,
  standing: number
): Promise<void> {
  const layer = printLayer();
  const card = document.createElement('div');
  card.className = 'paper-modal wide letterhead-doc';
  card.innerHTML =
    `<div class="paper-head letterhead"><span>PERSONNEL REVIEW</span><span>SUB-OFFICE 7</span></div>`;
  const body = document.createElement('div');
  body.className = 'paper-body typewriter';
  const q = review.quote ? `“${review.quote}”` : '';
  body.innerHTML =
    `<div class="field-label">STRENGTHS</div><div>${esc(review.strengths)}</div>` +
    `<div class="field-label" style="margin-top:8px">AREAS OF GROWTH</div><div>${esc(review.growth)}</div>` +
    (q ? `<div class="field-label" style="margin-top:8px">FROM THE FILE</div><div><span class="quoted">${esc(q)}</span>` +
      (review.quote_comment ? ` — ${esc(review.quote_comment)}` : '') + `</div>` : '') +
    `<hr class="rule">` +
    `<div>STANDING ${standingDelta >= 0 ? '+' + standingDelta : standingDelta} → ${standing}</div>`;
  card.appendChild(body);
  const zone = document.createElement('div');
  zone.className = 'slip-stamp-zone';
  body.appendChild(zone);
  const foot = document.createElement('div');
  foot.className = 'paper-foot';
  card.appendChild(foot);
  layer.appendChild(card);
  const tone = review.decision === 'PROBATION' ? 'red' : review.decision === 'PROMOTE' ? 'blue' : 'ink';
  window.setTimeout(() => {
    slamStamp(zone, stampEl(review.decision, tone), () => deps.bus.emit('audio:thunk'));
  }, 500);
  return new Promise((resolve) => {
    foot.appendChild(
      holdSlamButton('ACKNOWLEDGE', () => {
        card.remove();
        resolve();
      })
    );
  });
}

// ---------------------------------------------------------------------------
// THE PURGE — windows shutter in order; the file closes; the copier warms up
// ---------------------------------------------------------------------------

export async function purgeCeremony(deps: CeremonyDeps, designation: string): Promise<void> {
  const tl = new Timeline();
  tl.at(0, () => {
    deps.bus.emit('chyron', 'PROCESSING CONCLUDES. THE WINDOWS WILL NOW CLOSE IN ORDER.');
    deps.sceneMgr.setDim(0.75, 0.5);
  });
  for (let i = 5; i >= 1; i--) {
    tl.at(1.2 + (5 - i) * 0.7, () => {
      deps.bus.emit('window:shutter', { windowIdx: i });
      deps.bus.emit('audio:thunk');
    });
  }
  tl.at(5.2, () => {
    deps.sceneMgr.setDim(0.55, 0.5);
    deps.bus.emit('chyron', `THE FILE OF ${designation} IS CLOSED. THE RECORD REMAINS. RECORDS ALWAYS REMAIN.`);
  });
  tl.at(7.4, () => {
    deps.bus.emit('chyron', 'PROCEED TO THE COPIER, REGISTRY OF THE PURGED. YOUR SUCCESSOR IS WARM.');
    deps.bus.emit('context', 'PROCEED TO THE COPIER — REGISTRY OF THE PURGED (FAR WEST, PAST THE ROTUNDA)');
  });
  await deps.director.play(tl);
}

// ---------------------------------------------------------------------------
// REISSUE — the photocopier produces a successor with your face
// ---------------------------------------------------------------------------

export async function reissueCeremony(deps: CeremonyDeps, r: ReissueResponse): Promise<void> {
  // the flash: one white frame, then the hall returns
  const layer = printLayer();
  const flash = document.createElement('div');
  flash.style.cssText =
    'position:absolute;inset:0;background:#F4F1E6;opacity:0;transition:opacity .12s linear;pointer-events:none';
  layer.appendChild(flash);
  deps.bus.emit('audio:thunk');
  if (!REDUCED()) {
    flash.style.opacity = '0.95';
    await delay(160);
    flash.style.opacity = '0';
  }
  await delay(250);
  flash.remove();
  deps.sceneMgr.snapDim();
  deps.player.setDesignation(r.designation, `STANDING ${r.standing}`);
  deps.bus.emit('windows:unshutter');
  deps.bus.emit('chyron', `IDENTITY REISSUED. ${r.designation}. PRIOR INCIDENT ON FILE.`);

  const card = document.createElement('div');
  card.className = 'paper-modal slim letterhead-doc';
  card.innerHTML =
    `<div class="paper-head letterhead"><span>REISSUANCE RECORD</span><span class="tab-num">COPY ${esc(r.suffix)}</span></div>`;
  const body = document.createElement('div');
  body.className = 'paper-body typewriter';
  body.innerHTML =
    `<div style="font-size:15px">${esc(r.designation)}</div>` +
    `<div style="font-size:11px;opacity:.8;margin:4px 0 8px">STANDING ${r.standing} — ${esc(r.grade)}</div>` +
    `<div>${esc(r.memo)}</div>`;
  const zone = document.createElement('div');
  zone.className = 'slip-stamp-zone';
  body.appendChild(zone);
  card.appendChild(body);
  const foot = document.createElement('div');
  foot.className = 'paper-foot';
  card.appendChild(foot);
  layer.appendChild(card);
  slamStamp(zone, stampEl('PRIOR INCIDENT ON FILE', 'red'), () => deps.bus.emit('audio:thunk'));

  // The predecessor's obituary permalink (contract §23.3) — the strongest share
  // link the reissue flow can hand back. Surfaced on the successor's first boot.
  const obituaryUrl = r.predecessor?.obituary_url;
  if (obituaryUrl) {
    const shareRow = document.createElement('div');
    shareRow.style.cssText = 'margin-top:10px;font-size:12px';
    shareRow.appendChild(document.createTextNode("YOUR PREDECESSOR'S RECORD IS PUBLIC: "));
    const a = document.createElement('a');
    a.href = obituaryUrl;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.textContent = obituaryUrl;
    a.style.cssText = 'color:var(--gold);word-break:break-all';
    shareRow.appendChild(a);
    body.appendChild(shareRow);
  }

  await new Promise<void>((resolve) => {
    const reg = r.predecessor?.registry;
    if (reg) {
      const readBtn = document.createElement('button');
      readBtn.type = 'button';
      readBtn.className = 'slam-btn secondary';
      readBtn.textContent = 'READ THE FILE OF WHO YOU WERE';
      readBtn.addEventListener('click', () => {
        readBtn.remove();
        const old = document.createElement('div');
        old.style.cssText = 'margin-top:10px;border:1px dashed currentColor;padding:8px 10px;font-size:12px';
        old.innerHTML =
          `<div class="field-label">${esc(reg.designation)} — STRUCK FROM PROCESSING</div>` +
          (reg.final_words ? `<div>FINAL RECORDED WORDS: <span class="quoted">“${esc(reg.final_words)}”</span></div>` : '') +
          (reg.epitaph ? `<div style="margin-top:4px">${esc(reg.epitaph)}</div>` : '');
        body.appendChild(old);
      });
      foot.appendChild(readBtn);
    }
    foot.appendChild(
      holdSlamButton('PROCEED AS YOURSELF (COPY)', () => {
        card.remove();
        resolve();
      })
    );
  });
}

// ---------------------------------------------------------------------------

function esc(s: string): string {
  const d = document.createElement('div');
  d.textContent = s ?? '';
  return d.innerHTML;
}

function fmtNo(n: number): string {
  return String(Math.max(0, n)).padStart(6, '0').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}
